import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.jsx";
import { BrowserRouter } from "react-router-dom";
import { UserProvider } from "./contexts/UserContext.jsx";
import { AuthProvider } from "./contexts/AuthContext.jsx";
import { ThemeProvider } from "./contexts/ThemeContext.jsx";
import { SettingsProvider } from "./contexts/SettingsContext.jsx";
import { SubscriptionProvider } from "./contexts/SubscriptionContext.jsx";
import { SuperAdminProvider } from "./contexts/SuperAdminContext.jsx";
import { StaffProvider } from "./contexts/StaffContext.jsx";
import { AppearanceProvider } from "./contexts/AppearanceContext.jsx";
import { initPwaInstall } from "./utils/pwaInstall.js";

const rootElement = document.getElementById("root");
initPwaInstall();

function configureStartupLoader() {
  const loader = document.getElementById("app-loader");
  if (!loader) return;

  if (document.documentElement.dataset.entry === "public") {
    document.documentElement.classList.remove("app-loading");
    loader.remove();
    window.__firmaflowMarkAppReady = () => {};
    return;
  }

  let dismissed = false;
  let routeReady = false;
  let pendingRequests = 0;
  let lastNetworkChange = performance.now();
  const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  const startedAt = Number(window.__firmaflowLoaderStartedAt) || performance.now();
  const minimumDuration = reducedMotion ? 100 : 1700;
  const originalFetch = window.fetch.bind(window);

  window.fetch = (...args) => {
    pendingRequests += 1;
    lastNetworkChange = performance.now();
    return originalFetch(...args).finally(() => {
      pendingRequests = Math.max(0, pendingRequests - 1);
      lastNetworkChange = performance.now();
    });
  };

  const dismiss = () => {
    if (dismissed) return;
    dismissed = true;
    window.fetch = originalFetch;
    document.documentElement.classList.remove("app-loading");
    loader.classList.add("is-ready");
    window.setTimeout(() => loader.remove(), reducedMotion ? 80 : 480);
  };

  const waitForInitialData = () => {
    if (dismissed || !routeReady) return;
    const now = performance.now();
    const durationSatisfied = now - startedAt >= minimumDuration;
    const networkSettled = pendingRequests === 0 && now - lastNetworkChange >= 350;
    if (durationSatisfied && networkSettled) {
      dismiss();
      return;
    }
    window.setTimeout(waitForInitialData, 75);
  };

  window.__firmaflowMarkAppReady = () => {
    routeReady = true;
    waitForInitialData();
  };

  // A failed application bundle should not trap the user behind an endless
  // overlay. This is a recovery guard, not the normal readiness path.
  window.setTimeout(dismiss, 15000);
}

configureStartupLoader();

if (rootElement) {
  // Production SEO documents contain a readable static snapshot. React replaces
  // it with the interactive application once the client bundle is ready.
  rootElement.replaceChildren();
  createRoot(rootElement).render(

    <StrictMode>
      <BrowserRouter>
        <SuperAdminProvider>
          <StaffProvider>
            <UserProvider>
              <AuthProvider>
                <AppearanceProvider>
                  <SubscriptionProvider>
                    <ThemeProvider>
                      <SettingsProvider>
                        <App />
                      </SettingsProvider>
                    </ThemeProvider>
                  </SubscriptionProvider>
                </AppearanceProvider>
              </AuthProvider>
            </UserProvider>
          </StaffProvider>
        </SuperAdminProvider>
      </BrowserRouter>
    </StrictMode>
  );

}
